import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_STATUSES } from "../../../utils/constants";
import statusService from "../../../services/ticketService/statusService";
import { StatusInterface } from "../../../models/TicketModule/Status";

interface DeleteStatusContext {
  previousStatuses: StatusInterface[];
  deletedStatus: StatusInterface | undefined;
}

const useDeleteStatus = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the status to delete
    DeleteStatusContext
  >({
    mutationFn: statusService.Remove, // Remove should take a string ID

    onMutate: (statusId: string) => {
      const previousStatuses =
        queryClient.getQueryData<StatusInterface[]>([
          CACHE_KEY_STATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<StatusInterface[]>(
        [
          CACHE_KEY_STATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousStatuses.filter((status) => status.id !== statusId) // Use statusId to filter
      );

      return {
        previousStatuses,
        deletedStatus: previousStatuses.find(
          (status) => status.id === statusId
        ),
      };
    },

    onError: (error, statusId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<StatusInterface[]>(
        [
          CACHE_KEY_STATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousStatuses
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteStatus;
